import type { jsPDF } from "jspdf";
import { pdfEmbeddedFontName, type SurfSpeakerNote, type SurfSpeakerNoteKind } from "@vida/surf-core";

export type SpeakerPdfRun = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  mark?: boolean;
};

export type SpeakerPdfBlock =
  | { kind: "h3"; runs: SpeakerPdfRun[] }
  | { kind: "p"; runs: SpeakerPdfRun[] };

const COLORS_DARK = {
  body: [226, 232, 240] as const,
  heading: [100, 116, 139] as const,
  strong: [248, 250, 252] as const,
  em: [56, 189, 248] as const,
  markText: [125, 211, 252] as const,
  markBg: [56, 189, 248] as const,
  muted: [148, 163, 184] as const,
} as const;

type SpeakerPdfColorSet = typeof COLORS_DARK | typeof COLORS_LIGHT;

export const COLORS_LIGHT = {
  body: [30, 41, 59] as const,
  heading: [71, 85, 105] as const,
  strong: [15, 23, 42] as const,
  em: [2, 132, 199] as const,
  markText: [12, 74, 110] as const,
  markBg: [125, 211, 252] as const,
  muted: [100, 116, 139] as const,
} as const;

type SpeakerPdfTheme = "dark" | "light";

function themeColors(theme: SpeakerPdfTheme) {
  return theme === "light" ? COLORS_LIGHT : COLORS_DARK;
}

const SURF_SANS = "Surf Inter";

/** Typography for portrait speaker script (independent scale — tune without resizing thumbnails). */
export const SPEAKER_PDF_TYPO_SCALE = 1.28;

export const SPEAKER_PDF_TYPO = {
  bodySize: 11.5 * SPEAKER_PDF_TYPO_SCALE,
  bodyLine: 5.6 * SPEAKER_PDF_TYPO_SCALE,
  paraGap: 2.2 * SPEAKER_PDF_TYPO_SCALE,
  h3Size: 9 * SPEAKER_PDF_TYPO_SCALE,
  h3Line: 5 * SPEAKER_PDF_TYPO_SCALE,
  h3GapBefore: 2 * SPEAKER_PDF_TYPO_SCALE,
} as const;

function surfFontName(weight: number): string {
  return pdfEmbeddedFontName(SURF_SANS, weight);
}

function pushRun(runs: SpeakerPdfRun[], run: SpeakerPdfRun): void {
  if (!run.text) return;
  const prev = runs[runs.length - 1];
  if (
    prev &&
    prev.bold === run.bold &&
    prev.italic === run.italic &&
    prev.mark === run.mark
  ) {
    prev.text += run.text;
    return;
  }
  runs.push(run);
}

function parseInlineSegment(
  text: string,
  style: Omit<SpeakerPdfRun, "text">,
): SpeakerPdfRun[] {
  const runs: SpeakerPdfRun[] = [];
  let i = 0;
  while (i < text.length) {
    const markOpen = text.indexOf("<mark", i);
    const bold = text.indexOf("**", i);
    const italic = text.indexOf("*", i);

    const candidates = [
      markOpen >= 0 ? { kind: "mark" as const, at: markOpen } : null,
      bold >= 0 ? { kind: "bold" as const, at: bold } : null,
      italic >= 0 && text[italic + 1] !== "*" ? { kind: "italic" as const, at: italic } : null,
    ].filter((c): c is { kind: "mark" | "bold" | "italic"; at: number } => c !== null);

    if (!candidates.length) {
      pushRun(runs, { text: text.slice(i), ...style });
      break;
    }

    candidates.sort((a, b) => a.at - b.at);
    const next = candidates[0]!;

    if (next.at > i) {
      pushRun(runs, { text: text.slice(i, next.at), ...style });
    }

    if (next.kind === "bold") {
      const end = text.indexOf("**", next.at + 2);
      if (end < 0) {
        pushRun(runs, { text: text.slice(next.at), ...style });
        break;
      }
      runs.push(...parseInlineSegment(text.slice(next.at + 2, end), { ...style, bold: true }));
      i = end + 2;
      continue;
    }

    if (next.kind === "italic") {
      const end = text.indexOf("*", next.at + 1);
      if (end < 0) {
        pushRun(runs, { text: text.slice(next.at), ...style });
        break;
      }
      runs.push(...parseInlineSegment(text.slice(next.at + 1, end), { ...style, italic: true }));
      i = end + 1;
      continue;
    }

    const gt = text.indexOf(">", next.at);
    const close = gt >= 0 ? text.indexOf("</mark>", gt + 1) : -1;
    if (gt < 0 || close < 0) {
      pushRun(runs, { text: text.slice(next.at), ...style });
      break;
    }
    runs.push(...parseInlineSegment(text.slice(gt + 1, close), { ...style, mark: true }));
    i = close + 7;
  }

  return runs;
}

function parseInline(text: string): SpeakerPdfRun[] {
  const normalized = text
    .replace(/<mark[^>]*>/gi, "<mark>")
    .replace(/<\/?mark>/gi, (m) => m.toLowerCase());
  return parseInlineSegment(normalized, {});
}

export function parseSpeakerScriptMarkdown(markdown: string): SpeakerPdfBlock[] {
  const trimmed = markdown.trim();
  if (!trimmed) return [];

  return trimmed
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const h3 = block.match(/^#{1,3}\s+(.+)$/s);
      if (h3) return { kind: "h3" as const, runs: parseInline(h3[1]!.trim()) };
      const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      const runs: SpeakerPdfRun[] = [];
      for (const line of lines) {
        if (runs.length) runs.push({ text: " " });
        runs.push(...parseInline(line));
      }
      return { kind: "p" as const, runs };
    });
}

function runColor(
  run: SpeakerPdfRun,
  kind: SpeakerPdfBlock["kind"],
  colors: SpeakerPdfColorSet,
): readonly [number, number, number] {
  if (run.mark) return colors.markText;
  if (run.bold) return colors.strong;
  if (run.italic) return colors.em;
  if (kind === "h3") return colors.heading;
  return colors.body;
}

/** Surf Sans embedded; italic uses cyan (no italic face on disk). */
function displayPart(part: string, kind: SpeakerPdfBlock["kind"]): string {
  return kind === "h3" ? part.toUpperCase() : part;
}

function textBaselineY(cy: number, fontSize: number): number {
  return cy + fontSize * 0.35;
}

function applyRunFont(pdf: jsPDF, run: SpeakerPdfRun, fontSize: number, kind: SpeakerPdfBlock["kind"]): void {
  let weight = 400;
  if (run.bold) weight = 700;
  else if (kind === "h3") weight = 600;
  pdf.setFont(surfFontName(weight), "normal");
  pdf.setFontSize(fontSize);
}

export type PageBreakResult = { cy: number; maxY: number };

export type DrawSpeakerScriptOptions = {
  x: number;
  y: number;
  w: number;
  maxY: number;
  padX?: number;
};

export type OnPageBreakFn = () => PageBreakResult;

/**
 * Draw formatted speaker script (mm). Flows across pages via `onPageBreak` — no clipping or truncation.
 * Returns final Y.
 */
export function drawSpeakerScriptRichText(
  pdf: jsPDF,
  markdown: string,
  opts: DrawSpeakerScriptOptions,
  onPageBreak?: OnPageBreakFn,
  theme: SpeakerPdfTheme = "dark",
): number {
  const colors = themeColors(theme);
  const padX = opts.padX ?? 0;
  const innerX = opts.x + padX;
  const innerW = opts.w - padX * 2;
  let cy = opts.y;
  let maxY = opts.maxY;

  const blocks = parseSpeakerScriptMarkdown(markdown);
  if (!blocks.length) {
    pdf.setFont(surfFontName(400), "normal");
    pdf.setFontSize(SPEAKER_PDF_TYPO.bodySize);
    pdf.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2]);
    pdf.text("(Nessun discorso speaker per questa slide.)", innerX, cy + 4);
    return cy + 8;
  }

  const { bodySize, bodyLine, paraGap, h3Size, h3Line, h3GapBefore } = SPEAKER_PDF_TYPO;

  const breakPage = (): boolean => {
    if (!onPageBreak) return false;
    const next = onPageBreak();
    cy = next.cy;
    maxY = next.maxY;
    return true;
  };

  const ensureSpace = (needed: number): boolean => {
    if (cy + needed <= maxY) return true;
    return breakPage();
  };

  for (const block of blocks) {
    const fontSize = block.kind === "h3" ? h3Size : bodySize;
    const lineHeight = block.kind === "h3" ? h3Line : bodyLine;
    if (block.kind === "h3") {
      if (!ensureSpace(h3GapBefore + lineHeight)) break;
      cy += h3GapBefore;
    }

    let cx = innerX;
    let lineRuns: SpeakerPdfRun[] = [];
    let aborted = false;

    const flushLine = () => {
      if (!lineRuns.length || aborted) return;
      if (!ensureSpace(lineHeight)) {
        aborted = true;
        lineRuns = [];
        return;
      }
      cx = innerX;
      for (const run of lineRuns) {
        if (!run.text || aborted) continue;
        applyRunFont(pdf, run, fontSize, block.kind);
        const [r, g, b] = runColor(run, block.kind, colors);
        pdf.setTextColor(r, g, b);

        const parts = run.text.split(/(\s+)/);
        for (const part of parts) {
          if (!part || aborted) continue;
          const display = displayPart(part, block.kind);
          const width = pdf.getTextWidth(display);
          if (cx + width > innerX + innerW && cx > innerX && part.trim()) {
            cy += lineHeight;
            if (!ensureSpace(lineHeight)) {
              aborted = true;
              return;
            }
            cx = innerX;
          }
          if (run.mark && part.trim()) {
            const markH = fontSize * 0.35;
            pdf.setFillColor(colors.markBg[0], colors.markBg[1], colors.markBg[2]);
            pdf.setGState(pdf.GState({ opacity: 0.35 }));
            pdf.rect(cx, cy + fontSize * 0.08, width, markH, "F");
            pdf.setGState(pdf.GState({ opacity: 1 }));
          }
          pdf.text(display, cx, textBaselineY(cy, fontSize));
          cx += width;
        }
      }
      lineRuns = [];
      cy += lineHeight;
    };

    for (const run of block.runs) {
      const chunks = run.text.split("\n");
      chunks.forEach((chunk, idx) => {
        if (idx > 0) flushLine();
        if (chunk) lineRuns.push({ ...run, text: chunk });
      });
    }
    flushLine();
    if (aborted) break;
    if (block.kind === "p") cy += paraGap;
  }

  return cy;
}

/** @deprecated Portrait export draws script without a panel chrome. */
export function drawSpeakerScriptPanel(
  pdf: jsPDF,
  rect: { x: number; y: number; w: number; h: number },
  markdown: string,
): void {
  drawSpeakerScriptRichText(pdf, markdown, {
    x: rect.x,
    y: rect.y + 5,
    w: rect.w,
    maxY: rect.y + rect.h - 5,
    padX: 5,
  });
}

const NOTE_KIND_ACCENT: Record<SurfSpeakerNoteKind, readonly [number, number, number]> = {
  deepdive: [56, 189, 248],
  data: [34, 197, 94],
  objection: [251, 146, 60],
  anecdote: [168, 85, 247],
  qa: [244, 114, 182],
  definition: [148, 163, 184],
  freeform: [45, 212, 191],
};

const NOTE_CARD_STYLE = {
  bg: [248, 250, 252] as const,
  title: [15, 23, 42] as const,
  pad: 3.5,
  gap: 4,
  accentW: 1.4,
  radius: 2,
  titleSize: 10.5 * SPEAKER_PDF_TYPO_SCALE * 0.82,
  titleLine: 4.8 * SPEAKER_PDF_TYPO_SCALE * 0.82,
};

function stripNoteBodyPlain(body: string): string {
  return body
    .replace(/^#+\s+/gm, "")
    .replace(/\*\*/g, "")
    .replace(/\*/g, "")
    .replace(/<mark[^>]*>/gi, "")
    .replace(/<\/mark>/gi, "")
    .trim();
}

function noteQuestionText(note: SurfSpeakerNote): string | undefined {
  return note.question?.trim() || note.source?.question?.trim() || undefined;
}

const NOTE_QUESTION_STYLE = {
  labelSize: 7.5 * SPEAKER_PDF_TYPO_SCALE * 0.82,
  labelLine: 3.5 * SPEAKER_PDF_TYPO_SCALE * 0.82,
  textSize: 9 * SPEAKER_PDF_TYPO_SCALE * 0.82,
  textLine: 4.2 * SPEAKER_PDF_TYPO_SCALE * 0.82,
  gap: 2,
};

export function measureSpeakerNoteCardHeight(
  pdf: jsPDF,
  note: SurfSpeakerNote,
  w: number,
): number {
  const innerW = w - NOTE_CARD_STYLE.pad * 2 - NOTE_CARD_STYLE.accentW - 1.5;
  let total = NOTE_CARD_STYLE.pad * 2 + 3;

  const question = noteQuestionText(note);
  if (question) {
    pdf.setFont(surfFontName(600), "normal");
    pdf.setFontSize(NOTE_QUESTION_STYLE.labelSize);
    total += NOTE_QUESTION_STYLE.labelLine + NOTE_QUESTION_STYLE.gap;
    pdf.setFont(surfFontName(400), "italic");
    pdf.setFontSize(NOTE_QUESTION_STYLE.textSize);
    const qLines = (pdf.splitTextToSize(question, innerW) as string[]).length;
    total += qLines * NOTE_QUESTION_STYLE.textLine + NOTE_QUESTION_STYLE.gap;
  }

  pdf.setFont(surfFontName(700), "normal");
  pdf.setFontSize(NOTE_CARD_STYLE.titleSize);
  const titleLines = (pdf.splitTextToSize(note.title, innerW) as string[]).length;
  const titleH = titleLines * NOTE_CARD_STYLE.titleLine;
  total += titleH;

  pdf.setFont(surfFontName(400), "normal");
  pdf.setFontSize(SPEAKER_PDF_TYPO.bodySize * 0.92);
  const bodyPlain = stripNoteBodyPlain(note.body);
  const bodyLines = bodyPlain
    ? (pdf.splitTextToSize(bodyPlain, innerW) as string[]).length
    : 1;
  const bodyH = bodyLines * SPEAKER_PDF_TYPO.bodyLine * 0.92;

  return total + bodyH;
}

export function drawSpeakerNoteCards(
  pdf: jsPDF,
  notes: SurfSpeakerNote[],
  opts: { x: number; y: number; w: number; maxY: number },
  onPageBreak?: () => { cy: number; maxY: number },
): number {
  if (!notes.length) return opts.y;

  let cy = opts.y;
  let maxY = opts.maxY;
  const { x, w } = opts;

  const breakPage = (): boolean => {
    if (!onPageBreak) return false;
    const next = onPageBreak();
    cy = next.cy;
    maxY = next.maxY;
    return true;
  };

  pdf.setFont(surfFontName(600), "normal");
  pdf.setFontSize(9.5 * SPEAKER_PDF_TYPO_SCALE);
  pdf.setTextColor(...COLORS_DARK.heading);
  if (cy + 6 > maxY && !breakPage()) return cy;
  pdf.text("Approfondimenti", x, cy + 4);
  cy += 8;

  for (const note of notes) {
    const cardH = measureSpeakerNoteCardHeight(pdf, note, w);
    if (cy + cardH > maxY && !breakPage()) break;
    if (cy + cardH > maxY) break;

    const accent = NOTE_KIND_ACCENT[note.kind];
    pdf.setFillColor(...NOTE_CARD_STYLE.bg);
    pdf.roundedRect(x, cy, w, cardH, NOTE_CARD_STYLE.radius, NOTE_CARD_STYLE.radius, "F");
    pdf.setFillColor(accent[0], accent[1], accent[2]);
    pdf.rect(x, cy, NOTE_CARD_STYLE.accentW, cardH, "F");

    const innerX = x + NOTE_CARD_STYLE.accentW + NOTE_CARD_STYLE.pad;
    const innerW = w - NOTE_CARD_STYLE.accentW - NOTE_CARD_STYLE.pad * 2;
    let innerY = cy + NOTE_CARD_STYLE.pad + NOTE_CARD_STYLE.titleLine * 0.75;

    const question = noteQuestionText(note);
    if (question) {
      pdf.setFont(surfFontName(600), "normal");
      pdf.setFontSize(NOTE_QUESTION_STYLE.labelSize);
      pdf.setTextColor(100, 116, 139);
      pdf.text("Domanda", innerX, innerY);
      innerY += NOTE_QUESTION_STYLE.labelLine + NOTE_QUESTION_STYLE.gap;

      pdf.setFont(surfFontName(400), "italic");
      pdf.setFontSize(NOTE_QUESTION_STYLE.textSize);
      pdf.setTextColor(71, 85, 105);
      const qLines = pdf.splitTextToSize(question, innerW) as string[];
      for (const line of qLines) {
        pdf.text(line, innerX, innerY);
        innerY += NOTE_QUESTION_STYLE.textLine;
      }
      innerY += NOTE_QUESTION_STYLE.gap;
    }

    pdf.setFont(surfFontName(700), "normal");
    pdf.setFontSize(NOTE_CARD_STYLE.titleSize);
    pdf.setTextColor(...NOTE_CARD_STYLE.title);
    const titleLines = pdf.splitTextToSize(note.title, innerW) as string[];
    for (const line of titleLines) {
      pdf.text(line, innerX, innerY);
      innerY += NOTE_CARD_STYLE.titleLine;
    }
    innerY += 1;

    const bodyMaxY = cy + cardH - NOTE_CARD_STYLE.pad;
    drawSpeakerScriptRichText(
      pdf,
      note.body,
      {
        x: innerX,
        y: innerY,
        w: innerW,
        maxY: bodyMaxY,
        padX: 0,
      },
      () => {
        breakPage();
        return { cy: innerY, maxY: bodyMaxY };
      },
      "light",
    );

    cy += cardH + NOTE_CARD_STYLE.gap;
  }

  return cy;
}
